import { reduce as reduceP } from "lodash";
import { groupBy } from "lodash/fp";

import useRollupContext from "@app/analytics/hooks/meetops/useRollupContext";
import useMetricsQuery from "@app/analytics/hooks/metrics/useMetricsQuery";
import getColorFromScore from "@app/analytics/MeetingPolicy/getColorFromScore";
import { getReadAvgData } from "@app/analytics/MeetingPolicy/readAverages";
import { MeetingPolicyMetric } from "@app/analytics/MeetingPolicy/types";
import useMeetingPolicy from "@app/analytics/MeetingPolicy/useMeetingPolicy";
import { multiParticipantMeeting } from "@app/analytics/meetops/filters";
import { MeetOpsQuery } from "@app/analytics/meetops/types";
import { parseMetricsDataListFromQueryResult } from "@app/analytics/utils";
import { DateRange } from "@app/common/types";

import BarChart from "./BarChart";
import MeetingPolicyModule from "./MeetingPolicyModule";
import {
  getBarSeries,
  getSeries,
  getSum,
  getWeightedAverage,
} from "./meetingPolicyUtils";
import { getMetricMetaById } from "../meetops/metrics";

const MEETING_DURATION_BUCKETS = {
  "0": "≤ 30 min",
  "1": "30-60 min",
  "2": "> 60 min",
};

type BucketSize = keyof typeof MEETING_DURATION_BUCKETS;

const getQuery = (
  dateRange: DateRange | null,
  peopleValue: "only_me" | "everyone",
  teamId?: string,
  userId?: string
): MeetOpsQuery | null => {
  if (!dateRange) return null;

  return {
    dateRanges: [dateRange],
    dimensions: ["observedDuration"],
    metrics: [
      "engagement",
      "meetingCount",
      "sentiment",
      "meetingScore",
      "observedParticipantCount",
      "onTimeRate",
      "meetingSizeBucket",
    ],
    filters: [multiParticipantMeeting()],
    people: peopleValue,
    userId,
    teamId,
  };
};

const getHighestAndLowest = (
  data: any[],
  selectedMetric: MeetingPolicyMetric
) => {
  const keys = Object.keys(data);
  const values = Object.values(data).map((v) => v[selectedMetric].value);
  const highest = keys[values.indexOf(Math.max(...values))];
  const lowest = keys[values.indexOf(Math.min(...values))];
  return { highest, lowest };
};

const getDataSeries = (data: any[], metric: MeetingPolicyMetric) => {
  return data.map((d) => ({
    dimension: d.dimension,
    ...d[metric],
  }));
};

const bucketByMeetingDuration = groupBy<any>((item): BucketSize => {
  if (item.observedDuration > 60 * 60) {
    return "2";
  } else if (item.observedDuration >= 30 * 60) {
    return "1";
  }
  return "0";
});

const getBucketedData = (metrics?: any[] | null) => {
  if (!metrics) return [];
  const bucketed = bucketByMeetingDuration(metrics);

  const reducedBucketed = reduceP(
    bucketed,
    (bucketArr, value, key) => {
      bucketArr.push({
        dimension: key,
        sentiment: getWeightedAverage(value, "sentiment"),
        engagement: getWeightedAverage(value, "engagement"),
        meetingScore: getWeightedAverage(value, "meetingScore"),
        onTimeRate: getWeightedAverage(value, "onTimeRate"),
        meetingCount: getSum(value, "meetingCount"),
      });
      return bucketArr;
    },
    [] as any[]
  );
  return reducedBucketed;
};

const MeetingDurationModule = () => {
  const { filterBy, teamId, userId } = useRollupContext();
  const viewingIndividualMetrics =
    filterBy === "individual" || filterBy === "me";
  const peopleValue = viewingIndividualMetrics ? "only_me" : "everyone";

  const { startDate, endDate, toggleState, selectedMetric } =
    useMeetingPolicy();
  const queryDateRange = startDate && endDate ? { startDate, endDate } : null;

  const meetingSizeQuery = useMetricsQuery(
    getQuery(queryDateRange, peopleValue, teamId, userId),
    { staleTime: 30 * 60 * 1000 }
  );
  const metricMeta = getMetricMetaById(selectedMetric);

  const bucketedData = getBucketedData(
    parseMetricsDataListFromQueryResult(meetingSizeQuery.data)[0]
  );
  const metricSeries = getBarSeries(
    "horizontal",
    "bar",
    Object.keys(MEETING_DURATION_BUCKETS),
    getDataSeries(bucketedData, selectedMetric),
    {
      name: metricMeta.displayName,
      hideLabels: !toggleState.chartLabels,
      fillColor: (d) => getColorFromScore(d ?? 0),
      formatDimension: (d: string) =>
        MEETING_DURATION_BUCKETS[d as keyof typeof MEETING_DURATION_BUCKETS],
    }
  );

  const textMetricSeries = toggleState.meetingCount
    ? [
        getBarSeries(
          "horizontal",
          "text",
          Object.keys(MEETING_DURATION_BUCKETS),
          getDataSeries(bucketedData, "meetingCount"),
          {
            name: "Meetings",
            formatDimension: (d: string) =>
              MEETING_DURATION_BUCKETS[
                d as keyof typeof MEETING_DURATION_BUCKETS
              ],
            formatString: (s) => (s ? `[${s}]` : null),
          }
        ),
      ]
    : [];

  const readMetricSeries = toggleState.readAverage
    ? [
        getBarSeries(
          "horizontal",
          "line",
          Object.keys(MEETING_DURATION_BUCKETS),
          getSeries(
            "observedDuration",
            selectedMetric,
            getReadAvgData("observedDuration")
          ) ?? [],
          {
            name: "Read Avg",
            formatDimension: (d: string) =>
              MEETING_DURATION_BUCKETS[
                d as keyof typeof MEETING_DURATION_BUCKETS
              ],
          }
        ),
      ]
    : [];

  const { highest, lowest } = getHighestAndLowest(bucketedData, selectedMetric);
  return (
    <MeetingPolicyModule
      title="Meeting Duration"
      highest={
        highest
          ? MEETING_DURATION_BUCKETS[
              highest as keyof typeof MEETING_DURATION_BUCKETS
            ]
          : "-"
      }
      lowest={
        lowest
          ? MEETING_DURATION_BUCKETS[
              lowest as keyof typeof MEETING_DURATION_BUCKETS
            ]
          : "-"
      }
    >
      <BarChart
        series={[metricSeries, ...textMetricSeries, ...readMetricSeries]}
        orientation="horizontal"
        layout={{ xAxis: { label: metricMeta.displayName } }}
      />
    </MeetingPolicyModule>
  );
};
//

export default MeetingDurationModule;
